import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * TaxRate model matching the tax_rates table.
 */
export interface TaxRate {
  id: string;
  name: string;
  rate: number;
  province: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaxRatePayload {
  name: string;
  rate: number;
  province?: string | null;
  is_default?: boolean;
}

export interface UpdateTaxRatePayload {
  name?: string;
  rate?: number;
  province?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const taxRatesKeys = {
  all: ['tax-rates'] as const,
  list: (includeInactive?: boolean) => [...taxRatesKeys.all, 'list', { includeInactive }] as const,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function mapTaxRate(item: Record<string, unknown>): TaxRate {
  const rawRate = item.rate as string | number;
  return {
    id: item.id as string,
    name: (item.name || '') as string,
    rate: typeof rawRate === 'string' ? parseFloat(rawRate) : (rawRate as number),
    province: (item.province ?? null) as string | null,
    isDefault: (item.is_default ?? item.isDefault ?? false) as boolean,
    isActive: (item.is_active ?? item.isActive ?? true) as boolean,
    createdAt: (item.created_at || item.createdAt || '') as string,
    updatedAt: (item.updated_at || item.updatedAt || '') as string,
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all tax rates for the current tenant.
 * Always includes inactive rates so the UI can show status.
 */
export function useTaxRates() {
  return useQuery({
    queryKey: taxRatesKeys.list(true),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        '/settings/tax-rates',
        { params: { include_inactive: 'true' } },
      );
      const raw = response.data.data || [];
      return raw.map(mapTaxRate);
    },
  });
}

/**
 * Create a new tax rate.
 */
export function useCreateTaxRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTaxRatePayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/settings/tax-rates',
        payload,
      );
      return mapTaxRate(response.data.data as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxRatesKeys.all });
    },
  });
}

/**
 * Update an existing tax rate.
 */
export function useUpdateTaxRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTaxRatePayload }) => {
      const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
        `/settings/tax-rates/${id}`,
        data,
      );
      return mapTaxRate(response.data.data as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxRatesKeys.all });
    },
  });
}

/**
 * Soft-delete (deactivate) a tax rate.
 */
export function useDeleteTaxRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/settings/tax-rates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxRatesKeys.all });
    },
  });
}
